import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { supabase } from '../../lib/supabase';
import type { Service } from '../../lib/supabase';

interface PageContent {
  [key: string]: string;
}

export default function Appointment() {
  const { t } = useTranslation();
  const [services, setServices] = useState<Service[]>([]);
  const [content, setContent] = useState<PageContent>({});
  const [loading, setLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState<any>(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    service_id: '',
    appointment_date: '',
    appointment_time: '',
    vehicle_info: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    loadData();
    loadSiteSettings();
  }, []);

  const loadSiteSettings = async () => {
    try {
      const { data } = await supabase
        .from('site_settings')
        .select('*')
        .single();

      if (data) {
        setSiteSettings(data);
      }
    } catch (error) {
      console.error('Error loading site settings:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [servicesRes, contentRes] = await Promise.all([
        supabase.from('services').select('*').eq('is_active', true).order('display_order'),
        supabase.from('page_content').select('*').eq('page_name', 'appointment')
      ]);

      setServices(servicesRes.data || []);

      const contentMap: PageContent = {};
      contentRes.data?.forEach((item) => {
        contentMap[item.section_key] = item.content;
      });
      setContent(contentMap);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Seçilen hizmetin bilgilerini bul
      const selectedService = services.find(s => s.id === formData.service_id);
      
      const { error } = await supabase
        .from('appointments')
        .insert([
          {
            customer_name: formData.customer_name,
            customer_email: formData.customer_email,
            customer_phone: formData.customer_phone,
            service_id: formData.service_id,
            service_type: selectedService?.title || 'Genel Hizmet',
            preferred_date: formData.appointment_date,
            preferred_time: formData.appointment_time,
            notes: formData.notes,
            status: 'pending'
          }
        ]);

      if (error) throw error;

      setSubmitStatus('success');
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        service_id: '',
        appointment_date: '',
        appointment_time: '',
        vehicle_info: '',
        notes: ''
      });

      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Error submitting appointment:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <i className="ri-loader-4-line text-4xl text-[#FCD34D] animate-spin"></i>
      </div>
    );
  }

  // Arka plan stili
  const backgroundStyle = siteSettings?.background_image_enabled && siteSettings?.background_image_url
    ? {
        backgroundImage: `url(${siteSettings.background_image_url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }
    : { backgroundColor: '#0A0A0A' };

  return (
    <div className="min-h-screen" style={backgroundStyle}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>

        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
            {content.hero_title || t('appointment.hero.title')}
          </h1>
          <p className="text-xl text-white max-w-2xl mx-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
            {content.hero_subtitle || t('appointment.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Appointment Form */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-[#1F1F1F] rounded-lg border border-gray-800 p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">{t('appointment.form.personalInfo')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('appointment.form.name')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#171717] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FCD34D] transition-colors text-sm"
                      placeholder={t('appointment.form.namePlaceholder')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('appointment.form.email')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                      className="w-full px-4 py-3 bg-[#171717] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FCD34D] transition-colors text-sm"
                      placeholder={t('appointment.form.emailPlaceholder')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('appointment.form.phone')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      className="w-full px-4 py-3 bg-[#171717] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FCD34D] transition-colors text-sm"
                      placeholder={t('appointment.form.phonePlaceholder')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('appointment.form.service')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.service_id}
                      onChange={(e) => setFormData({ ...formData, service_id: e.target.value })}
                      className="w-full px-4 py-3 bg-[#171717] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FCD34D] transition-colors text-sm cursor-pointer"
                      required
                    >
                      <option value="">{t('appointment.form.selectService')}</option>
                      {services.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Appointment Details */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">{t('appointment.form.appointmentDetails')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('appointment.form.date')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.appointment_date}
                      onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 bg-[#171717] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FCD34D] transition-colors text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('appointment.form.time')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.appointment_time}
                      onChange={(e) => setFormData({ ...formData, appointment_time: e.target.value })}
                      className="w-full px-4 py-3 bg-[#171717] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FCD34D] transition-colors text-sm cursor-pointer"
                      required
                    >
                      <option value="">{t('appointment.form.selectTime')}</option>
                      <option value="08:00">08:00</option>
                      <option value="09:00">09:00</option>
                      <option value="10:00">10:00</option>
                      <option value="11:00">11:00</option>
                      <option value="12:00">12:00</option>
                      <option value="13:00">13:00</option>
                      <option value="14:00">14:00</option>
                      <option value="15:00">15:00</option>
                      <option value="16:00">16:00</option>
                      <option value="17:00">17:00</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Vehicle Information */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">{t('appointment.form.vehicleInfo')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('appointment.form.brand')}
                    </label>
                    <input
                      type="text"
                      value={formData.vehicle_info}
                      onChange={(e) => setFormData({ ...formData, vehicle_info: e.target.value })}
                      className="w-full px-4 py-3 bg-[#171717] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FCD34D] transition-colors text-sm"
                      placeholder={t('appointment.form.brandPlaceholder')}
                    />
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('appointment.form.notes')}
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 bg-[#171717] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FCD34D] transition-colors text-sm"
                  placeholder={t('appointment.form.notesPlaceholder')}
                />
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="whitespace-nowrap w-full px-8 py-4 bg-[#FCD34D] text-black font-bold rounded-lg hover:bg-[#fcd34ddd] transition-all cursor-pointer text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      {t('appointment.form.booking')}
                    </span>
                  ) : (
                    <span>
                      <i className="ri-calendar-check-line mr-2"></i>
                      {t('appointment.form.submit')}
                    </span>
                  )}
                </button>

                {/* Success Message */}
                {submitStatus === 'success' && (
                  <div className="mt-4 p-4 bg-green-500/10 border border-green-500 rounded-lg flex items-center gap-3">
                    <i className="ri-checkbox-circle-line text-2xl text-green-500"></i>
                    <div>
                      <p className="text-green-500 font-semibold">Randevunuz başarıyla oluşturuldu!</p>
                      <p className="text-green-400 text-sm">En kısa sürede sizinle iletişime geçeceğiz.</p>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-3">
                    <i className="ri-error-warning-line text-2xl text-red-500"></i>
                    <div>
                      <p className="text-red-500 font-semibold">Bir hata oluştu!</p>
                      <p className="text-red-400 text-sm">Lütfen tekrar deneyin veya bizi arayın.</p>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#1F1F1F] p-6 rounded-lg border border-gray-800 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-[#FCD34D]/10 rounded-lg mx-auto mb-4">
                <i className="ri-phone-line text-2xl text-[#FCD34D]"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{t('appointment.contact.call')}</h3>
              <p className="text-gray-400">+90 545 745 5075</p>
            </div>
            <div className="bg-[#1F1F1F] p-6 rounded-lg border border-gray-800 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-[#FCD34D]/10 rounded-lg mx-auto mb-4">
                <i className="ri-mail-line text-2xl text-[#FCD34D]"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">E-posta Gönderin</h3>
              <p className="text-gray-400">mrrbzkrt1987@gmail.com</p>
            </div>

            <div className="bg-[#1F1F1F] p-6 rounded-lg border border-gray-800 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-[#FCD34D]/10 rounded-lg mx-auto mb-4">
                <i className="ri-time-line text-2xl text-[#FCD34D]"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{t('appointment.contact.hours')}</h3>
              <p className="text-gray-400 text-sm whitespace-pre-line">{t('appointment.contact.hoursText')}</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}